import { useEffect, useRef } from 'react';
import { Button } from '@roger/ui-components';
import { Maximize2, Minimize2, Minus, X } from 'lucide-react';
import { useStore } from '../../store';
import { NEW_MESSAGE_WINDOW } from '../../store/slices/chatWindows';
import {
  closeAndFocusDock,
  minimizeAndFocusTab,
} from '../../hooks/chat/chatFocus';
import { ChannelMenu } from './ChannelMenu';
import { MOTION } from './chatMotion';
import { ConversationHeaderText } from './ConversationHeaderText';
import { PatientBand } from './PatientBand';
import { WindowBadge } from './WindowBadge';
import { WindowBody } from './WindowBody';
import { useConversationHeader } from './windowHeader';

/** One floating conversation (or the New message window): 320 x 460, 520 x 640 expanded. */
export function ChatWindow({ id }: { id: string }) {
  const isNew = id === NEW_MESSAGE_WINDOW;
  const expanded = useStore(state => state.chatWindows.expanded === id);
  const closeChatWindow = useStore(state => state.closeChatWindow);
  const toggleChatWindowExpanded = useStore(
    state => state.toggleChatWindowExpanded
  );
  const setChatWindowView = useStore(state => state.setChatWindowView);
  // Same query key as the body's: one request, shared by the cache.
  const { header, card, title, subtitle } = useConversationHeader(id, true);
  const view = useStore(state => state.windowViews[id] ?? 'conversation');
  const ref = useRef<HTMLDivElement>(null);
  const lastView = useRef(view);
  useEffect(() => {
    if (lastView.current === view) return;
    lastView.current = view;
    // The menu item or Back that changed the view is gone with it; without a
    // home, Esc would find focus on the page.
    const active = document.activeElement;
    if (!active || active === document.body) ref.current?.focus();
  }, [view]);
  const sizeLabel = expanded ? 'Make smaller' : 'Make larger';

  return (
    <div
      ref={ref}
      role='dialog'
      data-chat-window={id}
      data-chat-surface=''
      tabIndex={-1}
      aria-modal='false'
      aria-labelledby={`chat-window-title-${id}`}
      // A file dropped on the header or a view other than the conversation
      // must not make the browser open it; the conversation body handles drops
      // meant for the composer.
      onDragOver={event => event.preventDefault()}
      onDrop={event => event.preventDefault()}
      className={`pointer-events-auto relative z-40 flex max-h-[calc(100vh-80px)] flex-col overflow-hidden rounded-t-[10px] outline-none border border-b-0 bg-background ${
        expanded ? 'h-[640px] w-[520px]' : 'h-[460px] w-[320px]'
      } ${MOTION.window}`}
    >
      <div className='flex h-[52px] shrink-0 items-center gap-2 border-b pl-3 pr-2'>
        <WindowBadge id={id} />
        <ConversationHeaderText
          card={card}
          title={title}
          subtitle={subtitle}
          titleId={`chat-window-title-${id}`}
        />
        {header && !isNew && (
          <ChannelMenu
            header={header}
            onShowMembers={() => setChatWindowView(id, 'members')}
            onShowAnnouncers={() => setChatWindowView(id, 'announcers')}
          />
        )}
        {/* A 520 px window does not fit beside the dock below 1280 px. */}
        <Button
          size='icon'
          variant='ghost'
          aria-label={sizeLabel}
          title={sizeLabel}
          className='hidden xl:inline-flex'
          onClick={() => toggleChatWindowExpanded(id)}
        >
          {expanded ? (
            <Minimize2 className='h-3.5 w-3.5' />
          ) : (
            <Maximize2 className='h-3.5 w-3.5' />
          )}
        </Button>
        <Button
          size='icon'
          variant='ghost'
          aria-label='Minimize'
          title='Minimize'
          onClick={() => minimizeAndFocusTab(id)}
        >
          <Minus className='h-3.5 w-3.5' />
        </Button>
        <Button
          size='icon'
          variant='ghost'
          aria-label='Close'
          title='Close'
          onClick={() => closeAndFocusDock(id)}
        >
          <X className='h-3.5 w-3.5' />
        </Button>
      </div>

      {header && card && header.patient_id && header.episode_id && (
        <PatientBand
          card={card}
          patientId={header.patient_id}
          episodeId={header.episode_id}
          channelId={id}
          variant={expanded ? 'wide' : 'window'}
          memberCount={header.member_count}
          onShowMembers={() => setChatWindowView(id, 'members')}
        />
      )}

      <WindowBody id={id} onGone={() => closeChatWindow(id)} />
    </div>
  );
}
